import { useEffect } from 'react';

import type { UserRow } from '../userViewModel';

import UserDetailPane from './UserDetailPane';

interface UserDetailOverlayProps {
  user: UserRow;
  onClose: () => void;
  onDeactivate?: (user: UserRow) => void;
  onReactivate?: (user: UserRow) => void;
  onEdit?: (user: UserRow) => void;
}

/**
 * Right-side drawer scrim that docks the User Detail Pane over the Users
 * table. Per `DetailOverlay.handoff.md`:
 *   - absolute inset-0 inside the table's relative wrapper (so the scrim
 *     covers the table card only, not the app chrome)
 *   - flex justify-end items-stretch → pane lands flush right, full height
 *   - 8px padding → pane floats with an even gutter
 *   - border-radius 12 → matches the underlying card so corners line up
 *   - z-index 5
 *   - scrim background: light `rgba(15, 25, 18, 0.16)` (desaturated dark
 *     green-black, on-brand vs flat black); dark `rgba(0, 0, 0, 0.45)`
 *     because a 16% green-black is nearly invisible on the dark page bg.
 *   - clicks on the scrim dismiss; clicks inside the pane stay open.
 *   - Esc closes; pane gets role="dialog" + aria-modal via UserDetailPane
 *     props (close handler restores focus implicitly via React unmount).
 */
export default function UserDetailOverlay({
  user,
  onClose,
  onDeactivate,
  onReactivate,
  onEdit,
}: UserDetailOverlayProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="nb-drawer-scrim-enter absolute inset-0 z-[5] flex justify-end items-stretch p-2 rounded-xl bg-[rgba(15,25,18,0.16)] dark:bg-black/45"
    >
      {/* Click inside the pane should stay open — stop the scrim's onClick. The
       *  dialog role + aria-modal + aria-labelledby live on the pane itself
       *  (UserDetailPane's <aside>) so the accessibility tree picks them up.
       *  This stop-propagation div is a plain layout flex wrapper. */}
      <div onClick={(e) => e.stopPropagation()} className="nb-drawer-enter flex">
        <UserDetailPane
          user={user}
          onClose={onClose}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
          onEdit={onEdit}
        />
      </div>
    </div>
  );
}
